import type {
  WebsiteColorToken,
  WebsiteFontId,
  WebsiteInterfaceLocale,
  WebsiteIssueCode,
  WebsiteResolvedTheme,
  WebsiteThemeOverride,
  WebsiteThemeSchema,
} from '../../types/website-templates';
import { WEBSITE_COLOR_TOKENS } from '../../types/website-templates';
import { contrastRatio, isWebsiteHexColor } from './contrast';
import { fontStackFor, isWebsiteFontId } from './fonts';
import { checkClosedKeys, isOneOf, isRecord, isStructuralKey } from './internal';

export interface WebsiteContrastPair {
  readonly foreground: WebsiteColorToken;
  readonly background: WebsiteColorToken;
  readonly minimum: number;
}

/** A9: 4.5:1 for text, 3:1 for accent. Mirrored by SQL. */
export const WEBSITE_CONTRAST_PAIRS: readonly WebsiteContrastPair[] = [
  { foreground: 'text', background: 'background', minimum: 4.5 },
  { foreground: 'text', background: 'surface', minimum: 4.5 },
  { foreground: 'onBrand', background: 'brand', minimum: 4.5 },
  { foreground: 'accent', background: 'background', minimum: 3.0 },
];

/** Platform-fixed; never part of a template or an override. */
export const WEBSITE_FIXED_COLORS = {
  danger: '#b42318',
  success: '#067647',
  focus: '#1d4ed8',
} as const;

export type WebsiteThemeIssue = Extract<
  WebsiteIssueCode,
  | 'structure_override'
  | 'invalid_slot'
  | 'unsupported_color_token'
  | 'unsupported_font'
  | 'contrast_insufficient'
>;

export type WebsiteThemeValidation =
  | { readonly ok: true; readonly override: WebsiteThemeOverride }
  | { readonly ok: false; readonly issues: readonly WebsiteThemeIssue[] };

export function resolveWebsiteTheme(
  schema: WebsiteThemeSchema,
  override: WebsiteThemeOverride,
): WebsiteResolvedTheme {
  const colors = { ...schema.defaults };
  for (const token of schema.colorTokens) {
    const value = override.colors?.[token];
    if (isWebsiteHexColor(value)) colors[token] = value.toLowerCase();
  }
  const heading = override.fonts?.heading;
  const body = override.fonts?.body;
  return {
    colors,
    fonts: {
      heading:
        heading && schema.fontOptions.heading.includes(heading)
          ? heading
          : schema.defaultFonts.heading,
      body: body && schema.fontOptions.body.includes(body) ? body : schema.defaultFonts.body,
    },
  };
}

export function findContrastFailures(
  colors: Readonly<Record<WebsiteColorToken, string>>,
): readonly WebsiteContrastPair[] {
  return WEBSITE_CONTRAST_PAIRS.filter(
    (pair) => contrastRatio(colors[pair.foreground], colors[pair.background]) < pair.minimum,
  );
}

/**
 * Validates and normalizes (lowercase hex) a theme override. Closed keys,
 * template-declared tokens and fonts only, and the resolved palette must pass
 * every contrast pair.
 */
export function validateThemeOverride(
  schema: WebsiteThemeSchema,
  value: unknown,
): WebsiteThemeValidation {
  if (!isRecord(value)) return { ok: false, issues: ['invalid_slot'] };
  const topIssue = checkClosedKeys(value, ['colors', 'fonts'], []);
  if (topIssue) return { ok: false, issues: [topIssue] };

  const issues: WebsiteThemeIssue[] = [];
  const colors: Partial<Record<WebsiteColorToken, string>> = {};
  if (value.colors !== undefined) {
    if (!isRecord(value.colors)) return { ok: false, issues: ['invalid_slot'] };
    for (const [token, raw] of Object.entries(value.colors)) {
      if (!isOneOf(WEBSITE_COLOR_TOKENS, token) || !schema.colorTokens.includes(token)) {
        issues.push(isStructuralKey(token) ? 'structure_override' : 'unsupported_color_token');
        continue;
      }
      if (!isWebsiteHexColor(raw)) {
        issues.push('invalid_slot');
        continue;
      }
      colors[token] = raw.toLowerCase();
    }
  }

  const fonts: { heading?: WebsiteFontId; body?: WebsiteFontId } = {};
  if (value.fonts !== undefined) {
    if (!isRecord(value.fonts)) return { ok: false, issues: ['invalid_slot'] };
    const fontsIssue = checkClosedKeys(value.fonts, ['heading', 'body'], []);
    if (fontsIssue) {
      issues.push(fontsIssue === 'invalid_slot' ? 'unsupported_font' : fontsIssue);
    } else {
      for (const role of ['heading', 'body'] as const) {
        const raw = value.fonts[role];
        if (raw === undefined) continue;
        if (!isWebsiteFontId(raw) || !schema.fontOptions[role].includes(raw)) {
          issues.push('unsupported_font');
          continue;
        }
        fonts[role] = raw;
      }
    }
  }

  if (issues.length > 0) return { ok: false, issues: dedupe(issues) };

  const override: WebsiteThemeOverride = {
    ...(Object.keys(colors).length > 0 ? { colors } : {}),
    ...(Object.keys(fonts).length > 0 ? { fonts } : {}),
  };
  const resolved = resolveWebsiteTheme(schema, override);
  if (findContrastFailures(resolved.colors).length > 0) {
    return { ok: false, issues: ['contrast_insufficient'] };
  }
  return { ok: true, override };
}

function dedupe<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

export type WebsiteCssVariable =
  | `--w-color-${'brand' | 'on-brand' | 'accent' | 'background' | 'surface' | 'text'}`
  | `--w-color-${'danger' | 'success' | 'focus'}`
  | `--w-font-${'heading' | 'body'}`;

const COLOR_VARIABLE: Readonly<Record<WebsiteColorToken, WebsiteCssVariable>> = {
  brand: '--w-color-brand',
  onBrand: '--w-color-on-brand',
  accent: '--w-color-accent',
  background: '--w-color-background',
  surface: '--w-color-surface',
  text: '--w-color-text',
};

/**
 * The only path from theme values to the DOM: a closed record of `--w-*`
 * custom properties applied through a React style object. Values are
 * re-checked here so a malformed DTO cannot inject CSS.
 */
export function themeToCssVariables(
  theme: WebsiteResolvedTheme,
  locale: WebsiteInterfaceLocale,
): Record<WebsiteCssVariable, string> {
  const variables = {
    '--w-color-danger': WEBSITE_FIXED_COLORS.danger,
    '--w-color-success': WEBSITE_FIXED_COLORS.success,
    '--w-color-focus': WEBSITE_FIXED_COLORS.focus,
  } as Record<WebsiteCssVariable, string>;

  for (const token of WEBSITE_COLOR_TOKENS) {
    const value = theme.colors[token];
    if (!isWebsiteHexColor(value)) throw new RangeError('invalid theme color');
    variables[COLOR_VARIABLE[token]] = value.toLowerCase();
  }
  for (const role of ['heading', 'body'] as const) {
    const font = theme.fonts[role];
    if (!isWebsiteFontId(font)) throw new RangeError('invalid theme font');
    variables[`--w-font-${role}`] = fontStackFor(font, locale);
  }
  return variables;
}
